import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, translations } from '../lib/i18n/translations';

const I18nContext = createContext(null);

function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);
}

function createTranslationFn(language) {
  return function translate(key, variables) {
    const fallback = typeof variables === 'string' ? variables : '';
    const resolvedVariables = typeof variables === 'object' ? variables : null;
    const localized = getByPath(translations[language], key);
    const value = localized !== undefined ? localized : getByPath(translations[DEFAULT_LANGUAGE], key);
    if (value === undefined) {
      return fallback || key;
    }
    if (typeof value !== 'string') return value;
    if (!resolvedVariables) return value;

    return Object.entries(resolvedVariables).reduce((acc, [name, replacement]) => {
      const token = `{{${name}}}`;
      return acc.split(token).join(String(replacement));
    }, value);
  };
}

function persistLanguage(nextLanguage) {
  try {
    localStorage.setItem('app-language', nextLanguage);
  } catch (err) {
    // no-op
  }

  if (typeof document !== 'undefined') {
    document.cookie = `app-language=${encodeURIComponent(nextLanguage)}; path=/; max-age=31536000; samesite=lax`;
  }

  if (typeof window !== 'undefined') {
    window.__ETC_LANGUAGE__ = nextLanguage;
  }
}

function normalizeLanguage(language) {
  return SUPPORTED_LANGUAGES.includes(language) ? language : DEFAULT_LANGUAGE;
}

export function I18nProvider({ children, initialLanguage = DEFAULT_LANGUAGE }) {
  const [language, setLanguage] = useState(() => normalizeLanguage(initialLanguage));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (language !== DEFAULT_LANGUAGE) return;

    try {
      const stored = localStorage.getItem('app-language');
      if (stored && SUPPORTED_LANGUAGES.includes(stored) && stored !== language) {
        setLanguage(stored);
        persistLanguage(stored);
      }
    } catch (err) {
      // no-op
    }
  }, [language]);

  function changeLanguage(nextLanguage) {
    if (!SUPPORTED_LANGUAGES.includes(nextLanguage)) return;
    setLanguage(nextLanguage);
    persistLanguage(nextLanguage);
  }

  const value = useMemo(
    () => ({
      language,
      setLanguage: changeLanguage,
      languages: SUPPORTED_LANGUAGES,
      t: createTranslationFn(language),
    }),
    [language]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (context) return context;

  return {
    language: DEFAULT_LANGUAGE,
    setLanguage: () => {},
    languages: SUPPORTED_LANGUAGES,
    t: createTranslationFn(DEFAULT_LANGUAGE),
  };
}

export function useI18nSafe() {
  const context = useContext(I18nContext);
  if (context) return context;

  return {
    language: DEFAULT_LANGUAGE,
    setLanguage: () => {},
    languages: SUPPORTED_LANGUAGES,
    t: createTranslationFn(DEFAULT_LANGUAGE),
  };
}
